import React, {useState, useContext, useEffect} from 'react'

//Components
import ContentLink from 'components/ContentLink' 
import Tree from 'components/Tree'
import {ExportIcon} from 'components/SASIcon'

//Styles
import styles from './TOC.module.scss'

//Context
import { LanguageContext } from 'context/language-context'

//Types
import {EvaluateNode} from 'types/toc'

//Util
import {childSelected, traverseNode, keyboardClick, focusElement, focusParent} from 'util/tree'

export interface Props {
  alias?: string,
  page: string,
  node: any,
  level: number,
  index: number,
  count: number,
  prevKey: string,
  selectedNode: EvaluateNode,
  top?:boolean
  setParentExpand?: (value: boolean) => void
}

const createItem = (alias: string | undefined, page: string, lang: string, node: any, top?: boolean) => {
  const {type, label, url, file, fragment, docset, id, version, cfr} = node

  let parsedFile = file
  if (file && !file.endsWith('.htm')) {
    parsedFile = file+'.htm'
  }
  switch (type) {
    case 'web':
      return <a className={`${styles.label} ${top ? styles.topNode : styles.childNode}`} href={url} target="_blank" rel="noopener noreferrer" tabIndex={-1}>{ label }<ExportIcon /></a>
    case 'entry':
      return  <ContentLink className={`${styles.label} ${top ? styles.topNode : styles.childNode}`} language={lang} docsetAlias={docset} page={parsedFile} tabIndex={-1} children={label}></ContentLink>
    case 'parent':
      if (file && docset) {
        return  <ContentLink className={styles.label} language={lang} docsetAlias={docset} page={parsedFile} tabIndex={-1} children={label}></ContentLink>
      } else {
        return <div className={styles.label} tabIndex={-1}>{ label } </div>
      }
    case 'docset':
      return  <ContentLink className={`${styles.label} ${top ? styles.topNode : styles.childNode}`} language={lang} docsetAlias={id} docsetCFR={cfr} page={parsedFile} fragment={fragment} tabIndex={-1} children={label}></ContentLink>
    case 'cdc':
      return docset ?  <ContentLink className={`${styles.label} ${top ? styles.topNode : styles.childNode}`} language={lang} docsetAlias={docset} page={parsedFile} fragment={fragment} tabIndex={-1} children={label} cdcAlias={node.id} cdcCFR={node.version}></ContentLink> 
        : 
        <a className={`${styles.label} ${top ? styles.topNode : styles.childNode}`} href={`./?cdcId=${id}&cdcVersion=${version}&locale=${lang}`} rel="noopener noreferrer" tabIndex={-1}>{ label }</a>
    default:
      return <span></span> 
  }
}

const createChild = (
    alias:string | undefined, 
    page: string, 
    node: any, 
    level:number, 
    prevKey:string, 
    selectedNode: EvaluateNode, 
    handleKeyDown: (e: React.KeyboardEvent) => void,
    setParentExpand?: (value: boolean) => void
  ) => {
    const {children} = node
    const count = children ? children.length : 0
    let childItems
    let index = 0
    if (children && children.length > 0) {
      childItems = children.map((child:any) => {
        index = index+1
        return (
          <TOCNode 
            alias={alias}  
            page={page} 
            node={child}
            level={level}
            index={index}
            count={count}
            prevKey={prevKey+'-'+level+'-'+index} 
            key={prevKey+'-'+level+'-'+index}
            selectedNode={selectedNode} 
            setParentExpand={setParentExpand}/>
        )
      })
    }
    return childItems
}

const TOCNode = ({
    alias,
    page,
    node,
    level,
    index,
    count,
    prevKey,
    selectedNode,
    top,
    setParentExpand = () => {},
  }: Props) => {
    const lang = useContext(LanguageContext)
    const [expanded, setExpanded] = useState(false)

    const setExpand = (value: boolean) => {
      setExpanded(value)
    }

    const clickHandler = (e: React.MouseEvent) => {
      focusElement(e)
      if (node.type === 'parent') {
        setExpand(!expanded)
        keyboardClick()
      }
    }

    const closeParent = ():void => {
      setParentExpand(false)
      focusParent()
    }

    const keypressHandler = (e: React.KeyboardEvent) => {
      switch (e.keyCode) {
        case 37: //left arrow
          if (expanded) {
            setExpanded(false)
          } else {
            closeParent()
          }
          break;
        case 38: //up arrow
          traverseNode( 'up')
          break;
        case 39: //right arrow
          setExpanded(true)
          break;
        case 40: //down arrow
          traverseNode('down')
          break;
        case 13: //enter key
          keyboardClick()
          break;
        case 32: //spacebar
          keyboardClick()
          break;
      }
    }

    let selected = selectedNode === node

    let childIsSelected = false;
    useEffect(() => {
      if (node.type === 'parent' && node.children && childSelected(selectedNode, node.children)) {
        setExpanded(true)
      }
    }, [node.type, node.children, selectedNode]);

    if (node.type === 'parent' && node.children) {
      childIsSelected = childSelected(selectedNode, node.children)
      if (!selected  && !expanded) {
        selected = childIsSelected
      }
    }
    

    return (
      <Tree.Item 
        label= {node.label}
        level={level}
        index={index}
        count={count}
        item={createItem(alias, page, lang, node, top)} 
        children={createChild(alias, page,node,level+1,prevKey, selectedNode, keypressHandler, setExpand)} 
        expanded={expanded} 
        selected={selected} 
        clickHandler={clickHandler}
        handleKeyDown={keypressHandler}
        childIsSelected={childIsSelected}/>
    )
  }
  
  export default TOCNode